import { decodeCreationOptions, decodeRequestOptions, encodeCredential } from './webauthn.ts';

export interface SoraneConfig {
  siteId: string;
  endpoint?: string;
  title?: string;
  lead?: string;
}

export class SoraneApi {
  private readonly base: string;

  constructor(private readonly config: SoraneConfig) {
    this.base = (config.endpoint ?? window.location.origin).replace(/\/$/, '');
  }

  private url(path: string): string {
    return `${this.base}/api/v1/sites/${this.config.siteId}${path}`;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(this.url(path), {
      credentials: 'include',
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
    const json = (await res.json()) as T & { error?: string; error_description?: string };
    if (!res.ok) throw new Error(json.error_description ?? json.error ?? res.statusText);
    return json;
  }

  async getSession(): Promise<{ role: string } | null> {
    try {
      return await this.request('/auth/session');
    } catch {
      return null;
    }
  }

  async login(): Promise<void> {
    const begin = await this.request<{ handle: string; publicKey: PublicKeyCredentialRequestOptions }>(
      '/auth/login/options',
      { method: 'POST', body: '{}' },
    );
    const cred = await navigator.credentials.get({
      publicKey: decodeRequestOptions(begin.publicKey),
    });
    if (!cred) throw new Error('Passkey cancelled');
    await this.request('/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({ handle: begin.handle, clientResponse: encodeCredential(cred) }),
    });
  }

  async register(): Promise<void> {
    const begin = await this.request<{ handle: string; publicKey: PublicKeyCredentialCreationOptions }>(
      '/auth/register/options',
      { method: 'POST', body: '{}' },
    );
    const cred = await navigator.credentials.create({
      publicKey: decodeCreationOptions(begin.publicKey),
    });
    if (!cred) throw new Error('Passkey cancelled');
    await this.request('/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify({ handle: begin.handle, clientResponse: encodeCredential(cred) }),
    });
  }

  async listThreads(): Promise<ThreadRow[]> {
    const res = await this.request<{ threads: ThreadRow[] }>('/threads');
    return res.threads;
  }

  async getThread(id: string): Promise<{ thread: ThreadRow; messages: MessageRow[] }> {
    return this.request(`/threads/${encodeURIComponent(id)}`);
  }

  async createThread(url: string, question: string): Promise<void> {
    await this.request('/threads', {
      method: 'POST',
      body: JSON.stringify({ url, question }),
    });
  }
}

export interface ThreadRow {
  id: string;
  ref: string;
  kind: string;
  subject: string | null;
  canonicalUrl: string | null;
  urlTitle: string | null;
  updatedAt: string;
}

export interface MessageRow {
  id: string;
  authorRole: string;
  body: string;
  createdAt: string;
  agentCredentialId?: string | null;
}